<!-- Copyright (c) 2009-2022. Authors: see NOTICE file.

 Licensed under the Apache License, Version 2.0 (the "License");
 you may not use this file except in compliance with the License.
 You may obtain a copy of the License at

      http://www.apache.org/licenses/LICENSE-2.0

 Unless required by applicable law or agreed to in writing, software
 distributed under the License is distributed on an "AS IS" BASIS,
 WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 See the License for the specific language governing permissions and
 limitations under the License.-->

<template>
<div class="image-selector-wrapper">
  <b-loading :is-full-page="false" :active="loading" />
  
  <template v-if="!loading" >
    <!--  :class="{active: alreadyAdded(image)}" -->
    <div class="card" v-for="image in images" :key="image.id">
      <div :class="{active: alreadyAddedMap[image.id]}" @click="moveToImage(image.id)">
        
        <div class="thumb-wrapper">
          <image-thumbnail :rotate="true" :image="image" :macro="true" :blindMode="blindMode" :size="512" :key="`${image.id}-macro-256`" :extra-parameters="{Authorization: 'Bearer ' + localShortTermToken}" />
          <div class="add-icon" @click.stop="addImage(image)">
            <i class="fa fa-plus-square" aria-hidden="true"></i>
          </div>
        </div>

        <!-- <div
          class="card-image"
          :style="'background-image: url(' + appendShortTermToken(previewUrl(imageThumbUrl(image)), shortTermToken) + ')'"
          @click="moveToImage(image.id)"
        >
        </div> -->
        <div class="scan-mode-wrapper">
          <div
            v-for="(value, idx) in stainingInfo[image.id]"
            :key="idx"
            class="scan-mode"
            :style="{ backgroundColor: value.colorCode, width: 100 / stainingInfo[image.id].length + '%' }"
            :title="value.name"
          ></div>
        </div>

        <div class="card-content">
          <div class="content" >
            <image-name :image="image" />
          </div>
        </div>
      </div>
    </div>
  </template>
</div>
</template>

<script>
import {get,syncMultiselectFilter} from '@/utils/store-helpers';
import {IMAGE_FORMAT} from '@/utils/image-utils';

import ImageName from '@/components/image/ImageName';
// import CytomineMultiselect from '@/components/form/CytomineMultiselect';
import {ImageGroupCollection, ImageInstanceCollection, TagCollection} from 'cytomine-client';
import _ from 'lodash';
import {appendShortTermToken} from '@/utils/token-utils.js';

const storeOptions = {rootModuleProp: 'storeModule'};
const localSyncMultiselectFilter = (filterName, options) => syncMultiselectFilter(null, filterName, options, storeOptions);
import projectModuleModel from '@/store/modules/project';
import ImageThumbnail from '@/components/image/ImageThumbnail';

export default {
  name: 'image-selector',
  components: {
    ImageName,
    ImageThumbnail,
    // CytomineMultiselect
  },
  data() {
    return {
      images: [],
      availableTags:[],
      imageGroups: [],
      searchString: '',
      selectedImageGroups: [],
      nbImagesDisplayed: 20,
      nbFilteredImages: 0,
      loading: true,
      error: false,

      isAdding: false, // 로딩 상태 추가
      maxImages: 25,
      
      imageCollection: {},
      stainingInfo: {},

      localShortTermToken: '', // 재로딩 방지를 위한 분리
    };
  },
  computed: {
    serverIp: get('serverInfo/serverIp'),
    project: get('currentProject/project'),
    blindMode() {
      return ((this.$store.state.currentProject.project || {}).blindMode) || false;
    },
    idProject() {
      // console.log('!!!!!!!!!!!!!!! CytomineProject idCase', this.$route.params.idCase);
      return this.$route.params.idCase;
    },
    shortTermToken: get('currentUser/shortTermToken'),
    selectedTags: localSyncMultiselectFilter('selectedTags', 'availableTags'),
    storeModule() {
      return this.$store.getters['currentProject/currentProjectModule'] + 'listImages';
    },
    viewerModule() {
      return this.$store.getters['currentProject/currentViewerModule'];
    },
    imageSelectorEnabled: {
      get() {
        const viewer = this.$store.getters['currentProject/currentViewer'];
        return viewer ? viewer.imageSelector : false;
        // return this.$store.getters['currentProject/currentViewer'].imageSelector;
      },
      set(value) {
        this.$store.commit(this.viewerModule + 'setImageSelector', value);
      }
    },
    viewerImagesIds() {
      const viewer = this.$store.getters['currentProject/currentViewer'];
      if (!viewer || !viewer.images) {
        return null;
      }
      return Object.values(viewer.images).map(image => image.imageInstance?.id);
    },
    availableImageGroups() {
      return [{id: 'null', name: this.$t('no-image-group')}, ...this.imageGroups];
    },
    currentViewer() {
      return this.$store.getters['currentProject/currentViewer'];
    },
    alreadyAddedMap() {
      if (!this.viewerImagesIds) return {};
      const map = {};
      this.images.forEach(img => {
        map[img.id] = this.viewerImagesIds.includes(img.id);
      });
      return map;
    },
  },
  watch: {
    searchString() {
      this.fetchImages();
    },
    selectedImageGroups() {
      this.fetchImages();
    },
    nbImagesDisplayed() {
      this.fetchImages();
    },
    async selectedTags(){
      if (!this.selectedTags.length) {
        this.images = [];
      }
      else {
        await this.fetchImages();
      }
    },
    currentViewer(newVal) {
      if (newVal && newVal.imageSelector) {
        this.loading = false;
      }
    },
    shortTermToken(newVal) {
      if (!this.localShortTermToken && newVal) {
        this.localShortTermToken = newVal;
      }
    },
  },
  methods: {
    appendShortTermToken,
    debounceSearchString: _.debounce(async function(value) {
      this.searchString = value;
    }, 500),
    async addImage(image) {
      // 1. 최대 개수 체크
      if (this.viewerImagesIds && this.viewerImagesIds.length >= this.maxImages) {
        this.$notify({ 
          type: 'warning', 
          text: this.$t('notif-warning-max-images-limit', { count: this.maxImages })
        });
        return;
      }

      // 2. 중복 클릭 방지 (쓰로틀링 역할)
      if (this.isAdding) return;
      this.isAdding = true;
    
      try {
        await image.fetch(); // refetch image to ensure we have latest version
        let slice = await image.fetchReferenceSlice();
        await this.$store.dispatch(this.viewerModule + 'addImage', {image, slices: [slice]});
      }
      catch(error) {
        console.log(error);
        this.$notify({type: 'error', text: this.$t('notif-error-add-viewer-image')});
      }
      finally {
        setTimeout(() => {
          this.isAdding = false;
        }, 500);
      }
    },
    async fetchImageCollection() {
      const imageDatas = await this.imageCollection.fetchPage(0);
      this.images = imageDatas._data;
    },
    async fetchStainingInfo() {
      this.stainingInfo = await this.project.fetchStainingInfo();
    },
    async fetchImages() {
      // if (loading) {
      //   this.loading = true;
      // }

      this.imageCollection = new ImageInstanceCollection({
        filterKey: 'case',
        filterValue: this.project.id,
      });

      try {
        await Promise.all([
          this.fetchImageCollection(),
          this.fetchStainingInfo(),
        ]);
      }
      catch (error) {
        console.error('이미지 또는 외부 결과 로드 실패:', error);
      }
      finally {
        this.loading = false; // 성공 여부와 관계없이 실행
      }


      //   let collection = new ImageInstanceCollection({
      //     filterKey: 'project',
      //     filterValue: this.project.id,
      //     max: this.nbImagesDisplayed
      //   });

      //   if(this.searchString) {
      //     collection['name'] = {
      //       ilike: encodeURIComponent(this.searchString)
      //     };
      //   }

      //   if(this.selectedTags.length > 0) {
      //     collection['tag'] = {
      //       in: this.selectedTags.map(option => option.id).join()
      //     };
      //   }

      //   if (this.selectedImageGroups.length > 0 && this.selectedImageGroups.length !== this.availableImageGroups.length) {
      //     collection['imageGroup'] = {
      //       in: this.selectedImageGroups.map(option => option.id).join()
      //     };
      //   }

      //   if (this.selectedImageGroups.length === 0) {
      //     this.images = [];
      //     console.log('log from sidebar - 1. this.images', this.images);
      //     this.nbFilteredImages = 0;
      //   }
      //   else {
      //     let data = (await collection.fetchPage(0));
      //     this.images = data.array;
      //     this.nbFilteredImages = data.totalNbItems;
      //     console.log('log from sidebar - 2. this.images', this.images);
      //   }
      // }
      // catch(error) {
      //   console.log(error);
      //   this.error = true;
      // }
      // // if (loading) {
      // //   this.loading = false;
      // // }
    },
    async fetchImageGroups() {
      this.imageGroups = (await ImageGroupCollection.fetchAll({
        filterKey: 'project',
        filterValue: this.project.id
      })).array.filter(group => group.numberOfImages > 0);
    },
    async fetchTags() {
      this.availableTags = [{id: 'null', name: this.$t('no-tag')}, ...(await TagCollection.fetchAll()).array];
    },

    more() {
      this.nbImagesDisplayed += 20;
    },

    // toggle() {
    //   this.imageSelectorEnabled = !this.imageSelectorEnabled;
    // },

    shortkeyHandler(key) {
      if (key === 'toggle-add-image') {
        this.toggle();
      }
    },

    alreadyAdded(image) {
      return this.viewerImagesIds.includes(image.id);
    },
    imageThumbUrl(image) {
      return image.thumbURL(256, IMAGE_FORMAT);
    },
    replaceLocalhostWithServerIp(str, serverIp) {
      if (!str)
        return '';
      return str.replace(/127\.0\.0\.1/g, serverIp);
    },
    previewUrl(url) {
      return this.replaceLocalhostWithServerIp(url, this.serverIp);
    },
    moveToImage(imageId) {
      this.$router.push({path: `/case/${this.$route.params.idCase}/image/${imageId}`}).catch(err => {
        if (err.name !== 'NavigationDuplicated') {
          throw err;
        }
      });
    },
    async loadProject() {
      try {
        if (!this.$store.state.projects[this.idProject]) { // module does not exist yet
          this.$store.registerModule(['projects', this.idProject], projectModuleModel);
        }
        await this.$store.dispatch('currentProject/loadProject', this.idProject);
        // this.loading = false;
      }
      catch (error) {
        console.log(error);
        if (error.response && error.response.status === 403) {
          this.permissionError = true;
        }
        else {
          this.notFoundError = true;
        }
      }
    },
  },
  async created() {
    this.loading = true;
    this.localShortTermToken = this.shortTermToken; // 초기 토큰 가져옴, 이후 리로딩 방지를 위함
    await this.loadProject();
    await Promise.all([
      this.fetchImageGroups(),
      this.fetchImages(),
      this.fetchTags()
    ]);
    console.log('after promise all');
    this.selectedImageGroups = this.availableImageGroups.slice();
    console.log('after selectedImageGroups');
    this.loading = false;
  },
  mounted() {
    this.$eventBus.$on('shortkeyEvent', this.shortkeyHandler);
  },
  beforeDestroy() {
    this.$eventBus.$off('shortkeyEvent', this.shortkeyHandler);
  }
};
</script>

<style scoped>
.image-selector-wrapper {
  width: 100%;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 5px;
  padding: 2px;
  overflow: hidden;
  box-sizing: border-box;
}

.header {
  padding: 0.75em;
  padding-bottom: 0;
  padding-top: 0;
  display: flex;
  justify-content: center;
}

.header div {
  display: flex;
  align-items: baseline;
}

.header .filter-label {
  margin-right: 0.5em;
}

.search-images {
  margin-right: 1em;
}

.search {
  margin-top: 0.25em;
}

.filters {
  display: flex;
  padding: 0;
  background: None;
}

.filter-label {
  font-size: 0.9em;
  margin-top: 0.7rem;
  margin-left: 1em;
  margin-right: 0.5em;
}

.delete {
  margin-top: 1.2rem;
}

.image-selector {
  width: 100%;
  overflow: auto;
  display: flex;
  align-items: center;
  flex: 1;
}

.card {
  display: block;
  box-sizing: border-box;
  margin: 0.2rem;
  overflow: hidden;
  min-width: 0;

  box-shadow: none;
  background-color: transparent;
  cursor: pointer;
}

.thumb-wrapper {
  position: relative;
  overflow: hidden;
  width: 100%;
}

.card-image {
  display: inline-block;
  width: 100%;
  height: 6rem;
  border: solid 1px #e4e4e4;

  background-position: center center;
  background-size: cover;
  background-repeat: no-repeat;
  background-color: white;
  border-bottom: 1px solid #ddd;

  position: relative;
}

.add-icon {
  position: absolute;
  top: 2px;
  right: 2px;
  z-index: 10;
  cursor: pointer;

  display: flex;
  justify-content: center;
  align-items: center;

  width: 24px;
  height: 24px;
  border-radius: 4px;

  background-color: rgba(255, 255, 255, 0.75);

  color: #4A4847;
  transition: color 0.1s ease;
}

.add-icon:hover {
  color: #007bff; 
}

.card-content {
  padding: 0.2rem;
  font-size: 0.8rem;
  overflow-wrap: break-word;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.space {
  margin-left: 0.5em;
}

.image-selector-button {
  background: #95b5db;
  border: 0.35rem solid white;
  position: absolute;
  bottom: 1rem;
  right: 1rem;
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 50%;
  text-align: center;
  line-height: 2.8rem;
  color: white;
  font-size: 1.8rem;
  box-sizing: border-box;
  box-shadow: 0 2px 3px rgba(10, 10, 10, 0.1), 0 0 0 1px rgba(10, 10, 10, 0.1);
  z-index: 100;
}

.active {
  box-shadow: 0 2px 3px rgba(39, 120, 173, 0.75), 0 0 0 1px rgba(39, 120, 173, 0.75);
  font-weight: 600;
}

.no-result {
  margin: 2em;
}

.scan-mode-wrapper {
  display: flex; /* 가로 정렬 */
  /* width: 100%; */
  height: 8px; /* 사각형 높이 */
}

.scan-mode {
  height: 100%;
}

</style>
